import { useOne, useResourceParams, useShow } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PRODUCT_TYPES } from '@/model/Logistic/PackageItem/ProductTypes';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';
import { Ticket } from '@/model/Ticket/Ticket';
import { queryKeys } from '@/repositories/queryKeys';
import { RESOURCES } from '@/Resources';

import { hasTaxFile } from './shipmentTaxFileUtils';

const useShipmentShow = () => {
  const { resource, id } = useResourceParams();
  const { userWorkArea } = useUserContext();
  const { hasPermission } = usePermissionContext();
  const [packageItems, setPackageItems] = useState<PackageItem[]>([]);
  const [blockChangeState, setBlockChangeState] = useState<{ id: string; message: string }[]>([]);
  const {
    result,
    query: { isLoading, refetch },
  } = useShow<Shipment>({
    resource: RESOURCES.SHIPMENTS,
    id: id,
  });
  const {
    result: ticketResult,
    query: { isLoading: ticketIsLoading },
  } = useOne<Ticket>({
    resource: RESOURCES.TICKETS,
    id: result?.ticket || '',
    queryOptions: {
      enabled: !!result && !!result.ticket,
    },
  });
  const {
    result: createdByResult,
    query: { isLoading: createdByIsLoading },
  } = useOne({
    resource: 'users',
    id: result?.created_by || '',
    queryOptions: {
      enabled: !!result && !!result.created_by,
      retry: false,
    },
  });
  const {
    result: technicianResult,
    query: { isLoading: technicianIsLoading },
  } = useOne({
    resource: 'empresas',
    id: result?.technician || '',
    queryOptions: {
      enabled: !!result && !!result.technician,
      retry: false,
    },
  });
  const loading = useMemo(
    () =>
      isLoading ||
      ticketIsLoading ||
      createdByIsLoading ||
      (!!result?.technician && technicianIsLoading),
    [isLoading, ticketIsLoading, createdByIsLoading, result?.technician, technicianIsLoading],
  );
  const itemsEditMode: 'enabled' | 'partial-with-news' | 'disabled' | 'partial' = useMemo(
    () =>
      hasPermission('edit')
        ? result?.status === PACKAGE_STATES.PENDIENTE
          ? 'enabled'
          : userWorkArea === USER_WORK_AREA.LOGISTICS &&
              (result?.status === PACKAGE_STATES.PREPARACION ||
                result?.status === PACKAGE_STATES.PREPARADO)
            ? 'partial-with-news'
            : 'disabled'
        : 'disabled',
    [hasPermission, result?.status, userWorkArea],
  );
  const canSend = useMemo(
    () =>
      packageItems.every(item =>
        item.product_type === PRODUCT_TYPES.UNIT
          ? item.execute_amount === 0 || item.unidad !== undefined
          : item.product_type === PRODUCT_TYPES.DEVICE
            ? item.execute_amount === 0 || item.device !== undefined
            : true,
      ),
    [packageItems],
  );
  const isPaid = useMemo(
    () =>
      packageItems
        .filter(item => item.item_shipped_reference === SHIPPING_ITEM_REFERENCE.VENTA_REPUESTO)
        .every(item => item.billing_number),
    [packageItems],
  );
  const collectionShow = resource?.name === RESOURCES.SPARE_PARTS_COLLECTIONS;
  const showQuantityAlert = useMemo(() => {
    if (collectionShow) return false;

    return packageItems.some(
      item => item.execute_amount === undefined || item.execute_amount === 0,
    );
  }, [collectionShow, packageItems]);
  /**
   * Refetch de paquete e items
   **/
  const queryClient = useQueryClient();
  const refetchResource = useCallback(async () => {
    await refetch();
    if (!result?.id) {
      return;
    }

    await queryClient.refetchQueries({
      queryKey: queryKeys.packageItems.byPackage(result.id),
    });
  }, [queryClient, refetch, result?.id]);
  /**
   * Metadata: indica si el envío tiene al menos un archivo tributario cargado.
   * Se usa para bloquear el avance a estado PREPARADO si no se ha subido el archivo.
   */
  const hasTaxFileUploaded = useMemo(
    () => (result?.id ? hasTaxFile(result.photos, result.id) : false),
    [result?.id, result?.photos],
  );

  return {
    id,
    result,
    ticket: ticketResult,
    createdBy: createdByResult,
    technician: technicianResult,
    loading,
    itemsEditMode,
    canSend,
    isPaid,
    collectionShow,
    showQuantityAlert,
    blockChangeState,
    setBlockChangeState,
    packageItems,
    setPackageItems,
    refetchResource,
    hasTaxFileUploaded,
  };
};

export default useShipmentShow;
